import { createClaimReader, decodeJwtPayload } from './chunk-R6KDNQYQ.js';
import { singleflight } from './chunk-7B3IWQPL.js';
import { TenantContext } from './chunk-U3RLC4GJ.js';
import { globalSingleton } from './chunk-N7SZ66QO.js';

// src/auth/auth0.ts
function buildAuth0Config(env) {
  return {
    domain: env.domain,
    clientId: env.clientId,
    authorizationParams: {
      redirect_uri: env.redirectUri ?? (typeof window !== "undefined" ? window.location.origin : void 0),
      audience: env.audience,
      scope: env.scope ?? "openid profile email offline_access",
      organization: env.organization
    },
    claimsNamespace: env.claimsNamespace ?? "https://authe.io/"
  };
}
function tenantContextFromClaims(payload, claimsNamespace = "https://authe.io/") {
  const reader = createClaimReader(claimsNamespace);
  const claims = reader.readAll(payload);
  return new TenantContext({
    customerId: claims.customer_id ?? null,
    orgId: claims.org_id ?? null,
    tenantId: claims.tenant_id ?? null,
    userId: claims.user_id ?? null
  });
}
var EMPTY_TENANCY_NAMES = {
  customerName: null,
  orgName: null,
  tenantName: null,
  userName: null
};
function nonEmptyName(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}
function tenancyNamesFromClaims(payload, claimsNamespace = "https://authe.io/") {
  const claims = createClaimReader(claimsNamespace).readAll(payload);
  return {
    customerName: nonEmptyName(claims.customer_name),
    orgName: nonEmptyName(claims.org_name),
    tenantName: nonEmptyName(claims.tenant_name),
    userName: nonEmptyName(claims.user_name)
  };
}
function readAutheClaims(payload, claimsNamespace = "https://authe.io/") {
  return createClaimReader(claimsNamespace).readAll(payload);
}
function tenantStoragePrefix(opts) {
  return `${opts.platform}-${opts.env}-${opts.sub}-${opts.orgId}-${opts.tenantId}`;
}
function storage(opts) {
  if (opts.getStorage) return opts.getStorage();
  return typeof localStorage !== "undefined" ? localStorage : void 0;
}
function pinRegion(alias, opts) {
  storage(opts)?.setItem(`${opts.prefix}_use_region_alias`, JSON.stringify(alias));
}
function readRegionPin(opts) {
  try {
    return JSON.parse(storage(opts)?.getItem(`${opts.prefix}_use_region_alias`) ?? '"default"');
  } catch {
    return "default";
  }
}
function clearRegionPin(opts) {
  storage(opts)?.removeItem(`${opts.prefix}_use_region_alias`);
}
async function switchOrganization(client, organization) {
  await client.loginWithRedirect({ authorizationParams: { organization, prompt: "login" } });
}
async function switchTenant(client, tenantId, options = {}) {
  const authorizationParams = { tenant_id: tenantId };
  try {
    await client.getAccessTokenSilently({ cacheMode: "off", authorizationParams });
  } catch (error) {
    if (!client.loginWithRedirect) throw error;
    await client.loginWithRedirect({ authorizationParams });
    return;
  }
  const reload = options.reload ?? (() => {
    if (typeof window !== "undefined") window.location.reload();
  });
  reload();
}

// src/auth/action-vocabulary.ts
var MANAGE_ACTION = "*";
var DEFAULT_SCOPE_ACTIONS = ["list", "show", "create", "edit", "delete"];
var ACTION_TO_SCOPE_ACTIONS = {
  read: ["list", "show"],
  list: ["list"],
  show: ["show"],
  create: ["create"],
  update: ["edit"],
  edit: ["edit"],
  delete: ["delete"],
  export: ["export"],
  invoke: ["invoke"],
  [MANAGE_ACTION]: DEFAULT_SCOPE_ACTIONS
};
function scopeActionsFor(action) {
  const key = action.trim().toLowerCase();
  return ACTION_TO_SCOPE_ACTIONS[key] ?? [key];
}
function actionsGrant(actions, wanted) {
  if (!actions || actions.length === 0) return false;
  if (actions.includes(MANAGE_ACTION)) return true;
  const target = new Set(scopeActionsFor(wanted));
  for (const held of actions) {
    for (const granted of scopeActionsFor(held)) {
      if (target.has(granted)) return true;
    }
  }
  return false;
}
function toScopeActions(matrix) {
  const out = {};
  for (const [group, actions] of Object.entries(matrix ?? {})) {
    if (actions?.includes(MANAGE_ACTION)) {
      out[group] = [MANAGE_ACTION];
      continue;
    }
    const expanded = /* @__PURE__ */ new Set();
    for (const action of actions ?? []) for (const scope of scopeActionsFor(action)) expanded.add(scope);
    out[group] = [...expanded];
  }
  return out;
}
var TEAM_GROUP_SCOPES = {
  team: {
    read: "teams:read",
    list: "teams:list",
    show: "teams:read",
    create: "teams:create",
    update: "teams:update",
    edit: "teams:update",
    delete: "teams:delete"
  },
  team_members: {
    read: "team-members:read",
    list: "team-members:list",
    show: "team-members:read",
    create: "team-members:create",
    update: "team-members:update",
    edit: "team-members:update",
    delete: "team-members:delete"
  },
  team_roles: {
    read: "team-roles:read",
    list: "team-roles:list",
    show: "team-roles:read",
    create: "team-roles:create",
    update: "team-roles:update",
    edit: "team-roles:update",
    delete: "team-roles:delete"
  },
  team_invites: {
    read: "team-invites:read",
    list: "team-invites:list",
    show: "team-invites:read",
    // An invite is sent and revoked, not created and deleted.
    create: "team-invites:send",
    update: "team-invites:resend",
    edit: "team-invites:resend",
    delete: "team-invites:revoke"
  },
  // Read-only by nature: there is no write verb to map.
  team_audit: {
    read: "team-audit:read",
    list: "team-audit:read",
    show: "team-audit:read"
  }
};
function teamScopeFor(group, action) {
  return TEAM_GROUP_SCOPES[group.trim()]?.[action.trim().toLowerCase()];
}
function splitScope(scope) {
  const i = scope.lastIndexOf(":");
  if (i <= 0 || i === scope.length - 1) return null;
  return [scope.slice(0, i), scope.slice(i + 1)];
}
function rolePermissionsGrant(rolePermissions, resource, action) {
  if (!rolePermissions) return false;
  if (actionsGrant(rolePermissions[resource], action)) return true;
  const scope = teamScopeFor(resource, action);
  if (!scope) return false;
  const parsed = splitScope(scope);
  if (!parsed) return false;
  const held = rolePermissions[parsed[0]];
  if (!held || held.length === 0) return false;
  return held.includes(MANAGE_ACTION) || held.includes(parsed[1]);
}

// src/auth/casbin.ts
var RBAC_MODEL = `
[request_definition]
r = sub, obj, act

[policy_definition]
p = sub, obj, act

[role_definition]
g = _, _

[policy_effect]
e = some(where (p.eft == allow))

[matchers]
m = g(r.sub, p.sub) && keyMatch(r.obj, p.obj) && regexMatch(r.act, p.act)
`;
var DEFAULT_RBAC_POLICY = "";
async function createAutheEnforcer(policyCsv = DEFAULT_RBAC_POLICY) {
  const moduleSpecifier = "casbin.js";
  let casbin;
  try {
    casbin = await import(
      /* @vite-ignore */
      moduleSpecifier
    );
  } catch {
    throw new Error(
      "casbin.js is required for createAutheEnforcer(). Install it, or use enforceFromRolePermissions() for the JWT fast-path."
    );
  }
  const model = casbin.newModel(RBAC_MODEL);
  const adapter = new casbin.MemoryAdapter(policyCsv);
  const enforcer = await casbin.newEnforcer(model, adapter);
  return {
    enforce: (sub, obj, act) => enforcer.enforce(sub, obj, act)
  };
}
function policyFromRolePermissions(role, rolePermissions) {
  const lines = [];
  for (const [resource, actions] of Object.entries(rolePermissions)) {
    for (const action of actions) {
      lines.push(`p, ${role}, ${resource}, (${action})`);
    }
  }
  return lines.join("\n");
}
function enforceFromRolePermissions(rolePermissions, resource, action) {
  return rolePermissionsGrant(rolePermissions, resource, action);
}
var permKey = (resource, action) => `${resource}\0${action}`;
function permissionSetFromRolePermissions(rolePermissions) {
  return { can: (resource, action) => enforceFromRolePermissions(rolePermissions, resource, action) };
}
async function materializePermissionSet(enforcer, subject, grid) {
  const granted = /* @__PURE__ */ new Set();
  await Promise.all(
    grid.resources.flatMap(
      (resource) => grid.actions.map(async (action) => {
        if (await enforcer.enforce(subject, resource, action)) granted.add(permKey(resource, action));
      })
    )
  );
  return { can: (resource, action) => granted.has(permKey(resource, action)) };
}
function keyMatch(key, pattern) {
  const star = pattern.indexOf("*");
  if (star === -1) return key === pattern;
  if (key.length > star) return key.slice(0, star) === pattern.slice(0, star);
  return key === pattern.slice(0, star);
}
function stripParens(act) {
  const m = /^\((.*)\)$/.exec(act.trim());
  return m ? m[1] : act.trim();
}
function actionMatches(action, pattern) {
  if (pattern === "*" || pattern === ".*") return true;
  try {
    return new RegExp(`^(?:${pattern})$`).test(action);
  } catch {
    return action === pattern;
  }
}
function parsePolicyCsv(csv) {
  const grants = [];
  const roleLinks = [];
  for (const raw of csv.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const [ptype, ...rest] = line.split(",").map((p) => p.trim());
    if (ptype === "p" && rest.length >= 3) {
      grants.push({ role: rest[0], resource: rest[1], action: stripParens(rest[2]) });
    } else if (ptype === "g" && rest.length >= 2) {
      roleLinks.push({ child: rest[0], parent: rest[1] });
    }
  }
  return { grants, roleLinks };
}
function parseRolePermissionsPolicy(byRole) {
  const grants = [];
  for (const [role, perms] of Object.entries(byRole)) {
    for (const [resource, actions] of Object.entries(perms)) {
      for (const action of actions) grants.push({ role, resource, action });
    }
  }
  return { grants, roleLinks: [] };
}
function parseRolePermissionsArray(entries) {
  const grants = [];
  for (const entry of entries) {
    const role = entry?.name;
    if (!role || !Array.isArray(entry.scopes)) continue;
    for (const scope of entry.scopes) {
      const parsed = splitScope2(String(scope));
      if (parsed) grants.push({ role, resource: parsed.resource, action: parsed.action });
    }
  }
  return { grants, roleLinks: [] };
}
function expandRoles(roleNames, roleLinks) {
  const result = new Set(roleNames);
  let changed = true;
  while (changed) {
    changed = false;
    for (const { child, parent } of roleLinks) {
      if (result.has(child) && !result.has(parent)) {
        result.add(parent);
        changed = true;
      }
    }
  }
  return result;
}
function permissionSetFromPolicy(roleNames, policy) {
  const roles = expandRoles(roleNames, policy.roleLinks);
  const grants = policy.grants.filter((g) => g.role === "*" || roles.has(g.role));
  return {
    can: (resource, action) => grants.some((g) => keyMatch(resource, g.resource) && actionMatches(action, g.action))
  };
}
async function loadPolicyFromUrl(url, options = {}) {
  const fetchImpl = options.fetchImpl ?? fetch;
  const res = await fetchImpl(url, { cache: "no-cache", signal: options.signal });
  if (!res.ok) throw new Error(`policy fetch failed: ${res.status} ${url}`);
  const text = await res.text();
  const looksCsv = url.endsWith(".csv") || /^\s*[pg]\s*,/m.test(text);
  if (looksCsv) return parsePolicyCsv(text);
  const json = JSON.parse(text);
  if (Array.isArray(json)) return parseRolePermissionsArray(json);
  return parseRolePermissionsPolicy(json);
}
function policyDataEnv(env) {
  return env === "local" ? "sandbox" : env;
}
function policyFileName(env, format) {
  return (format ?? "json") === "csv" ? `policy-${env}.csv` : "role_permissions.json";
}
function resolvePolicyUrl(source) {
  const base = source.baseUrl.replace(/\/+$/, "");
  if (source.objectPath) return `${base}/${source.objectPath.replace(/^\/+/, "")}`;
  const env = policyDataEnv(source.env);
  return `${base}/public/GET/cdn/${env}/global/permissions-data/${policyFileName(env, source.format)}`;
}
var policies = globalSingleton("auth/policies", () => ({
  cache: /* @__PURE__ */ new Map(),
  inflight: singleflight()
}));
async function loadPolicy(source, options = {}) {
  const url = typeof source === "string" ? source : resolvePolicyUrl(source);
  if (options.force) {
    policies.cache.delete(url);
    policies.inflight.forget(url);
  }
  const cached = policies.cache.get(url);
  if (cached) return cached;
  return policies.inflight.run(url, async () => {
    const policy = await loadPolicyFromUrl(url, options);
    policies.cache.set(url, policy);
    return policy;
  });
}
function clearPolicyCache() {
  policies.cache.clear();
}
function permissionSetFromClaims(claims, policy) {
  if (policy) return permissionSetFromPolicy(claims?.roles ?? [], policy);
  return permissionSetFromRolePermissions(claims?.role_permissions);
}
function splitActions(action) {
  const a = action.trim().replace(/^\((.*)\)$/, "$1").trim();
  if (a === "") return [];
  if (a === ".*") return ["*"];
  return a.split("|").map((s) => s.trim()).filter(Boolean);
}
function applicableGrants(roleNames, policy) {
  const roles = expandRoles(roleNames, policy.roleLinks);
  return policy.grants.filter((g) => g.role === "*" || roles.has(g.role));
}
function scopesFromPolicy(roleNames, policy) {
  const out = /* @__PURE__ */ new Set();
  for (const g of applicableGrants(roleNames, policy)) {
    for (const action of splitActions(g.action)) out.add(`${g.resource}:${action}`);
  }
  return [...out].sort();
}
function rolePermissionsFromPolicy(roleNames, policy) {
  const acc = {};
  for (const g of applicableGrants(roleNames, policy)) {
    for (const action of splitActions(g.action)) (acc[g.resource] ??= /* @__PURE__ */ new Set()).add(action);
  }
  return sortedMap(acc);
}
function splitScope2(scope) {
  const i = scope.lastIndexOf(":");
  if (i <= 0 || i === scope.length - 1) return null;
  return { resource: scope.slice(0, i), action: scope.slice(i + 1) };
}
function iterateClaimScopes(claim, onScope) {
  if (Array.isArray(claim)) {
    for (const entry of claim) {
      if (typeof entry === "string") {
        onScope(entry);
        continue;
      }
      const scopes = entry?.scopes;
      if (Array.isArray(scopes)) for (const s of scopes) onScope(String(s));
    }
  } else if (claim && typeof claim === "object") {
    for (const [resource, actions] of Object.entries(claim)) {
      if (Array.isArray(actions)) for (const a of actions) onScope(`${resource}:${a}`);
    }
  }
}
function sortedMap(acc) {
  const out = {};
  for (const key of Object.keys(acc).sort()) out[key] = [...acc[key]].sort();
  return out;
}
function scopesFromRolePermissionsClaim(claim) {
  const out = /* @__PURE__ */ new Set();
  iterateClaimScopes(claim, (s) => out.add(s));
  return [...out].sort();
}
function rolePermissionsMapFromClaim(claim) {
  const acc = {};
  iterateClaimScopes(claim, (scope) => {
    const parsed = splitScope2(scope);
    if (parsed) (acc[parsed.resource] ??= /* @__PURE__ */ new Set()).add(parsed.action);
  });
  return sortedMap(acc);
}
var EMPTY_VIEW = { scopes: [], rolePermissions: {}, source: "none", awaitingSignIn: [] };
function resolvePermissionsView(input) {
  const roles = input.roles ?? [];
  const policyScopes = input.policy ? scopesFromPolicy(roles, input.policy) : [];
  const tokenScopes = scopesFromRolePermissionsClaim(input.rolePermissionsClaim);
  if (tokenScopes.length) {
    const carried = new Set(tokenScopes);
    return {
      scopes: tokenScopes,
      rolePermissions: rolePermissionsMapFromClaim(input.rolePermissionsClaim),
      source: "token",
      awaitingSignIn: policyScopes.filter((scope) => !carried.has(scope))
    };
  }
  if (!policyScopes.length) return EMPTY_VIEW;
  return {
    scopes: policyScopes,
    rolePermissions: input.policy ? rolePermissionsFromPolicy(roles, input.policy) : {},
    source: "policy",
    awaitingSignIn: []
  };
}
async function loadPermissionsView(options) {
  let policy = null;
  if (options.policySource) {
    try {
      policy = await loadPolicy(options.policySource, {
        fetchImpl: options.fetchImpl,
        signal: options.signal
      });
    } catch {
      policy = null;
    }
  }
  let rolePermissionsClaim;
  if (options.accessToken) {
    const payload = decodeJwtPayload(options.accessToken);
    if (payload) {
      rolePermissionsClaim = createClaimReader(options.namespace).readClaim(
        payload,
        "role_permissions"
      );
    }
  }
  return resolvePermissionsView({ roles: options.roles, policy, rolePermissionsClaim });
}

export { ACTION_TO_SCOPE_ACTIONS, DEFAULT_RBAC_POLICY, DEFAULT_SCOPE_ACTIONS, EMPTY_TENANCY_NAMES, MANAGE_ACTION, RBAC_MODEL, TEAM_GROUP_SCOPES, actionsGrant, buildAuth0Config, clearPolicyCache, clearRegionPin, createAutheEnforcer, enforceFromRolePermissions, keyMatch, loadPermissionsView, loadPolicy, loadPolicyFromUrl, materializePermissionSet, parsePolicyCsv, parseRolePermissionsArray, parseRolePermissionsPolicy, permissionSetFromClaims, permissionSetFromPolicy, permissionSetFromRolePermissions, pinRegion, policyDataEnv, policyFromRolePermissions, readAutheClaims, readRegionPin, resolvePermissionsView, resolvePolicyUrl, rolePermissionsFromPolicy, rolePermissionsGrant, rolePermissionsMapFromClaim, scopeActionsFor, scopesFromPolicy, scopesFromRolePermissionsClaim, switchOrganization, switchTenant, teamScopeFor, tenancyNamesFromClaims, tenantContextFromClaims, tenantStoragePrefix, toScopeActions };
//# sourceMappingURL=chunk-6ZKBE4IX.js.map
//# sourceMappingURL=chunk-6ZKBE4IX.js.map